<template>
  <Teleport to="body">
    <div v-if="show" ref="panelRef" class="ai-refine-popover" :style="panelStyle">
      <div class="ai-refine-popover__arrow" :style="arrowStyle" />
      <AiCustomizationPanel
        :disabled="disabled"
        :show-close="true"
        :title="title"
        :generate-button-label="generateButtonLabel"
        :show-attachments="showAttachments"
        @generate="emit('generate', $event)"
        @close="emit('close')"
      />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onBeforeUnmount } from "vue";
import AiCustomizationPanel from "./AiCustomizationPanel.vue";

const PANEL_WIDTH = 395;

const props = withDefaults(
  defineProps<{
    show: boolean;
    triggerEl: HTMLElement | null;
    disabled?: boolean;
    title?: string;
    generateButtonLabel?: string;
    showAttachments?: boolean;
  }>(),
  {
    title: "AI Refine",
    generateButtonLabel: "Generate Message",
    showAttachments: true,
  }
);

const emit = defineEmits<{
  generate: [payload: any];
  close: [];
}>();

const panelRef = ref<HTMLElement | null>(null);
const panelStyle = ref<Record<string, string>>({
  position: "fixed",
  zIndex: "11000",
  width: `${PANEL_WIDTH}px`,
  visibility: "hidden",
});
const arrowStyle = ref<Record<string, string>>({});

function updatePosition() {
  if (!props.triggerEl || !panelRef.value) return;

  const triggerRect = props.triggerEl.getBoundingClientRect();
  // Use offsetHeight for accurate rendered height after paint
  const panelHeight = panelRef.value.offsetHeight || 360;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const gap = 8;

  const spaceBelow = vh - triggerRect.bottom;
  const spaceAbove = triggerRect.top;
  const showAbove = spaceAbove > spaceBelow && spaceAbove >= panelHeight + gap;

  const top = showAbove ? triggerRect.top - panelHeight - gap : triggerRect.bottom + gap;

  // Center the panel on the trigger button, clamped to viewport
  let left = triggerRect.left + triggerRect.width / 2 - PANEL_WIDTH / 2;
  left = Math.max(8, Math.min(left, vw - PANEL_WIDTH - 8));

  const arrowLeft = triggerRect.left + triggerRect.width / 2 - left - 8;

  panelStyle.value = {
    position: "fixed",
    top: `${top}px`,
    left: `${left}px`,
    zIndex: "11000",
    width: `${PANEL_WIDTH}px`,
    visibility: "visible",
  };

  arrowStyle.value = {
    left: `${Math.max(8, Math.min(arrowLeft, PANEL_WIDTH - 24))}px`,
    ...(showAbove
      ? { top: "100%", borderTopColor: "#e8e8f0", borderBottomColor: "transparent" }
      : { top: "-8px", borderBottomColor: "#e8e8f0", borderTopColor: "transparent" }),
  };
}

watch(
  () => props.show,
  async (val) => {
    if (val) {
      // Reset to hidden so panel renders off-screen before we measure
      panelStyle.value = {
        position: "fixed",
        zIndex: "11000",
        width: `${PANEL_WIDTH}px`,
        visibility: "hidden",
        top: "-9999px",
        left: "-9999px",
      };
      await nextTick();
      // requestAnimationFrame ensures the browser has painted and offsetHeight is accurate
      requestAnimationFrame(() => {
        updatePosition();
      });
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    } else {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    }
  }
);

onBeforeUnmount(() => {
  window.removeEventListener("scroll", updatePosition, true);
  window.removeEventListener("resize", updatePosition);
});
</script>

<style scoped>
.ai-refine-popover {
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
  border-radius: 10px;
  background: #fff;
}

.ai-refine-popover__arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
}
</style>
